import React, { useState, useEffect } from 'react';
import { Drawer, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { TaskDrawerProvider, useTaskDrawer } from './TaskDrawerContext';
import { useProjectTour } from '../tour';
import TaskActivityLog from './components/TaskActivityLog';
import TaskCollaborators from './components/TaskCollaborators';
import TaskReviewers from './components/TaskReviewers';
import TaskDescription from './components/TaskDescription';
import TaskFooter from './components/TaskFooter';
import TaskHeader from './components/TaskHeader';
import TaskMetadata from './components/TaskMetadata';
import TaskSubtasks from './components/TaskSubtasks';
import TaskTabs from './components/TaskTabs';
import AiTaskOverview from './components/AiTaskOverview';
import TaskAiSummaryModal from './components/TaskAiSummaryModal';
import { formatHHMMSS } from './utils/timeFormatters';

dayjs.extend(relativeTime);

function TaskDrawerShell() {
    const { isOpen: isTourOpen, activeTourId, currentStep } = useProjectTour();
    const {
        open, onClose, closeAll, task, loading, loadError, loadTask,
        hasMask, drawerStack, itemType, setItemType, mode, handleSafeClose,
        isBottomCollapsed, toggleBottomCollapsed,
        isBottomExpanded, toggleBottomExpanded,
        zIndex,
    } = useTaskDrawer();
    const [isFullSummaryOpen, setIsFullSummaryOpen] = useState(false);
    const [activeAiSummaryData, setActiveAiSummaryData] = useState(null);

    const safeClose = () => handleSafeClose ? handleSafeClose(closeAll || onClose) : (closeAll || onClose)?.();
    const safeCloseAll = () => handleSafeClose ? handleSafeClose(closeAll) : closeAll?.();

    // Auto-toggle itemType during create tour so user sees task vs milestone fields highlighted live
    useEffect(() => {
        if (!isTourOpen || activeTourId !== 'createTask' || mode !== 'create') return;
        if (currentStep === 7 && itemType !== 'task') {
            setItemType('task');
        } else if ((currentStep === 8 || currentStep === 9) && itemType !== 'milestone') {
            setItemType('milestone');
        }
    }, [isTourOpen, activeTourId, currentStep, mode, itemType, setItemType]);

    const isStacked = drawerStack && drawerStack.length > 1;
    const isMilestone = (task?.itemType === 'milestone') || (itemType === 'milestone');

    return (
        <Drawer
            open={open}
            onClose={safeClose}
            mask={hasMask !== false}
            zIndex={zIndex || 1100}
            width="min(900px, 96vw)"
            closable={false}
            styles={{ body: { padding: 0, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' } }}
            bodyStyle={{ padding: 0, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}
            className="task-detail-drawer relative font-body bg-white dark:bg-[#1f1f1f] text-[#1c1e21] dark:text-white"
        >
            <style>{`
                .task-detail-drawer .ant-drawer-body {
                    padding: 0 !important;
                    overflow: hidden !important;
                    display: flex !important;
                    flex-direction: column !important;
                    height: 100% !important;
                }
            `}</style>
            <div className="h-1.5 w-full bg-gradient-to-r from-[#03488A] to-[#006591] shrink-0" />

            {loading && !task && <TaskDrawerLoading isMilestone={isMilestone} />}
            {loadError && <TaskDrawerError onRetry={loadTask} isMilestone={isMilestone} />}

            {task && (
                <div className="flex flex-col h-full min-h-0 flex-1 overflow-hidden bg-white dark:bg-[#1f1f1f]">
                    {isStacked ? (
                        <div className="absolute -left-12 top-6 flex flex-col gap-2 z-10">
                            <button
                                onClick={onClose}
                                className="w-10 h-10 bg-ed-surface-container-lowest dark:bg-[#262626] rounded-full shadow-lg flex items-center justify-center hover:bg-primary hover:text-on-primary transition-all text-ed-on-surface dark:text-slate-200 border border-ed-surface-container-high dark:border-[#3a3a3a]"
                                title="Back to parent task"
                            >
                                <span className="material-symbols-outlined">arrow_back</span>
                            </button>
                            <button
                                onClick={safeCloseAll}
                                className="w-10 h-10 bg-ed-surface-container-lowest dark:bg-[#262626] rounded-full shadow-lg flex items-center justify-center hover:bg-error hover:text-white transition-all text-ed-on-surface dark:text-slate-200 border border-ed-surface-container-high dark:border-[#3a3a3a]"
                                title="Close drawer"
                            >
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={safeClose}
                            className="absolute -left-12 top-6 w-10 h-10 bg-ed-surface-container-lowest dark:bg-[#262626] rounded-full shadow-lg flex items-center justify-center hover:bg-primary hover:text-on-primary transition-all text-ed-on-surface dark:text-slate-200 border border-ed-surface-container-high dark:border-[#3a3a3a] z-10"
                        >
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    )}

                    <TaskHeader />
                    <div
                        className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar flex"
                        style={{ willChange: 'scroll-position', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}
                    >
                        <div className="flex-1 px-5 py-3.5 space-y-4 min-w-0 w-full">
                            {mode !== 'create' && (
                                <AiTaskOverview onOpenFullSummary={(data) => {
                                    if (data) setActiveAiSummaryData(data);
                                    setIsFullSummaryOpen(true);
                                }} />
                            )}
                            <TaskMetadata />
                            <TaskDescription />
                            <TaskSubtasks />
                        </div>
                    </div>

                    {/* Collapsible & Stretchable Comments & Activity Dock */}
                    <div className="shrink-0 border-t border-[#e7e8e9] dark:border-[#303030] bg-white dark:bg-[#1f1f1f] flex flex-col">
                        {/* Interactive Bar with Stretch-Up & Collapse Controls */}
                        <div
                            className="flex items-center justify-between px-5 py-2 bg-slate-50/90 dark:bg-[#181818] hover:bg-slate-100/90 dark:hover:bg-[#232323] border-b border-[#e7e8e9]/70 dark:border-[#2d2d2d] transition-colors select-none text-xs text-slate-600 dark:text-slate-300"
                        >
                            <div
                                onClick={toggleBottomCollapsed}
                                className="flex items-center gap-2.5 font-semibold cursor-pointer"
                                title={isBottomCollapsed ? 'Click to expand Comments & Activity' : 'Click to collapse Comments & Activity'}
                            >
                                <span className="flex items-center gap-1.5 text-slate-700 dark:text-slate-200">
                                    <span className="material-symbols-outlined text-[15px] text-[#03488a] dark:text-[#a7c8ff]">chat</span>
                                    <span>Comments ({task?.comments?.length || 0})</span>
                                </span>
                                <span className="text-slate-300 dark:text-slate-600">•</span>
                                <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                                    <span className="material-symbols-outlined text-[15px] text-amber-500">history</span>
                                    <span>Activity ({task?.activityLog?.length || 0})</span>
                                </span>
                            </div>

                            <div className="flex items-center gap-2.5">
                                {/* Stretch-Up / Expand Icon Button */}
                                {!isBottomCollapsed && (
                                    <button
                                        type="button"
                                        onClick={toggleBottomExpanded}
                                        className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold text-[#03488a] dark:text-[#a7c8ff] bg-blue-50/70 hover:bg-blue-100 dark:bg-blue-950/40 dark:hover:bg-blue-900/60 border border-blue-200/80 dark:border-blue-800/60 transition-colors shadow-2xs"
                                        title={isBottomExpanded ? 'Restore default height' : 'Stretch up (expand comments section)'}
                                    >
                                        <span className="material-symbols-outlined text-[15px]">
                                            {isBottomExpanded ? 'close_fullscreen' : 'open_in_full'}
                                        </span>
                                        <span>{isBottomExpanded ? 'Restore' : 'Stretch Up'}</span>
                                    </button>
                                )}

                                {/* Collapse / Minimize Toggle */}
                                <button
                                    type="button"
                                    onClick={toggleBottomCollapsed}
                                    className="flex items-center gap-1 text-[11px] font-bold text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition-colors cursor-pointer"
                                    title={isBottomCollapsed ? 'Click to expand' : 'Click to collapse'}
                                >
                                    <span>{isBottomCollapsed ? 'Expand' : 'Collapse'}</span>
                                    <span className="material-symbols-outlined text-[17px] transition-transform duration-200">
                                        {isBottomCollapsed ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                                    </span>
                                </button>
                            </div>
                        </div>

                        {/* Dock Body */}
                        {!isBottomCollapsed && (
                            <div className={`overflow-y-auto custom-scrollbar px-5 pt-3 pb-2 space-y-3 transition-all duration-300 ease-in-out ${isBottomExpanded ? 'h-[75vh] max-h-[75vh]' : 'max-h-[36vh]'}`}>
                                <TaskTabs />
                                <TaskActivityLog />
                            </div>
                        )}
                    </div>

                    <TaskFooter />
                    {task && (
                        <TaskAiSummaryModal
                            taskId={task._id}
                            taskTitle={task.title}
                            open={isFullSummaryOpen}
                            initialData={activeAiSummaryData || task.aiSummary?.structuredData}
                            onClose={() => setIsFullSummaryOpen(false)}
                        />
                    )}
                </div>
            )}
        </Drawer>
    );
}

function TaskDrawerLoading({ isMilestone }) {
    const label = isMilestone ? 'Loading milestone details...' : 'Loading details...';
    return (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-ed-surface-container-lowest/80 dark:bg-[#0f172a]/80 z-50">
            <Spin size="large" indicator={<LoadingOutlined style={{ fontSize: 36, color: '#03488A' }} spin />} />
            <span className="mt-4 text-xs font-semibold text-ed-on-surface-variant tracking-wider uppercase">{label}</span>
        </div>
    );
}

function TaskDrawerError({ onRetry, isMilestone }) {
    return (
        <div className="absolute inset-0 flex flex-col items-center justify-center p-6 bg-ed-surface-container-lowest dark:bg-[#0f172a] z-50 text-center">
            <div className="w-16 h-16 rounded-full bg-error-container flex items-center justify-center mb-4">
                <span className="material-symbols-outlined text-error text-3xl">error</span>
            </div>
            <h3 className="text-lg font-bold text-ed-on-surface">Failed to load {isMilestone ? 'milestone' : 'details'}</h3>
            <p className="text-sm text-ed-on-surface-variant mt-2 max-w-sm">
                There was an error retrieving the {isMilestone ? 'milestone' : 'task'} details. Please check your connection and try again.
            </p>
            <button onClick={onRetry} className="mt-6 px-5 py-2.5 brand-gradient text-on-primary text-xs font-bold uppercase tracking-wider rounded-xl shadow-md hover:shadow-lg transition-all active:scale-95">
                Retry Fetch
            </button>
        </div>
    );
}

/**
 * TaskDetailDrawer
 * Manages a stack of task drawers for parent â†’ subtask navigation.
 * The bottom-most drawer has the backdrop mask; subsequent layers stack on top without masks.
 */
export default function TaskDetailDrawer(props) {
    const { taskId, open, onClose, mode = 'view', parentId: initialParentId = null, initialMilestoneId = null } = props || {};
    const [stack, setStack] = useState([]);

    // Sync stack when the drawer opens/closes or the root task changes
    React.useEffect(() => {
        if (open) {
            if (mode === 'create') {
                setStack([{ id: 'new', mode: 'create', title: 'New Task', parentId: initialParentId, initialMilestoneId }]);
            } else if (taskId) {
                setStack([{ id: taskId, mode, title: 'Task' }]);
            }
        } else {
            const timer = setTimeout(() => setStack([]), 350);
            return () => clearTimeout(timer);
        }
    }, [open, taskId, mode, initialParentId, initialMilestoneId]);

    const pushTask = (newId, newMode = 'view', newTitle = 'Subtask', parentId = null) => {
        setStack((prev) => [...prev, { id: newId, mode: newMode, title: newTitle, parentId }]);
    };

    const updateStackItem = (id, title, itemType) => {
        setStack((prev) => prev.map((item) => (item.id === id ? { ...item, title: title || item.title, itemType: itemType || item.itemType } : item)));
    };

    const popTask = () => {
        setStack((prev) => {
            if (prev.length <= 1) {
                onClose();
                return prev;
            }
            return prev.slice(0, -1);
        });
    };

    const popTo = (index) => {
        setStack((prev) => prev.slice(0, index + 1));
    };

    return (
        <>
            {stack.map((item, index) => {
                const isTop = index === stack.length - 1;
                // Only the bottom-most drawer shows the dark backdrop mask
                const hasMask = index === 0;

                return (
                    <TaskDrawerProvider
                        key={`${item.id}-${item.initialMilestoneId || item.parentId || ''}`}
                        value={{
                            ...props,
                            taskId: item.id,
                            mode: item.mode,
                            parentId: item.parentId ?? initialParentId,
                            initialMilestoneId: item.initialMilestoneId ?? initialMilestoneId,
                            open: true,
                            onClose: isTop ? popTask : undefined,
                            // closeAll always closes the entire drawer regardless of stack depth
                            closeAll: onClose,
                            pushTask,
                            popTask,
                            popTo,
                            updateStackItem,
                            drawerStack: stack.slice(0, index + 1),
                            isTop,
                            hasMask,
                        }}
                    >
                        <TaskDrawerShell />
                    </TaskDrawerProvider>
                );
            })}
        </>
    );
}

export { formatHHMMSS };

